/**
 * The pipeline spine at the top of a review's case file.
 *
 * The six stages every ticket walks, read left to right: Proposed, Plan, Build,
 * Review, QA, Done. It reads `PIPELINE_STAGES` and `pipelineStageOf`, the same
 * vocabulary the list row and the stage pie read, so the three can never
 * disagree. Behind the work is done, the stage it stands in is current (in the
 * accent only while a person is owed a move), and ahead is quiet.
 */
import type { WorkItem } from "@yagni/shared";
import { Fragment } from "react";
import {
  isNeedsYou,
  PIPELINE_STAGES,
  PIPELINE_STAGE_LABEL,
  pipelineStageOf,
  stageOutcomeOf,
} from "./workStage.js";

export function StageSpine({ item }: { item: WorkItem }) {
  const current = PIPELINE_STAGES.indexOf(pipelineStageOf(item));
  // A finished ticket has walked every stage it is going to: Done is behind
  // it, not ahead of it.
  const finished = stageOutcomeOf(item) === "shipped";
  const waiting = isNeedsYou(item);
  return (
    <div className="wk-spine" aria-label="Stage" data-testid="work-stage-spine">
      {PIPELINE_STAGES.map((stage, index) => {
        const state =
          finished || index < current ? "done" : index === current ? "current" : "future";
        return (
          <Fragment key={stage}>
            {index > 0 && <span className="wk-spine-sep" aria-hidden />}
            <span
              className={
                `wk-spine-st is-${state}` +
                (state === "current" && waiting ? " is-needsyou" : "")
              }
              data-testid={`spine-${stage}`}
              aria-current={state === "current" ? "step" : undefined}
            >
              {PIPELINE_STAGE_LABEL[stage]}
            </span>
          </Fragment>
        );
      })}
    </div>
  );
}
